import React, {useRef, useState} from 'react';

import {FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  RadioGroup,
  Typography,
} from '@mui/material';
import {Stack} from '@mui/system';
import {merge} from 'lodash';
import {Controller, useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import CountryInput from 'Components/CountryInput';
import {RhfWatcher} from 'Components/RhfUtils';
import {ZmCheckbox} from 'Components/ZmCheckbox';
import {ZmTextField} from 'Components/ZmInputs';
import {ZmRadio} from 'Components/ZmRadio';
import {capitalizeStr} from 'Utils';
import {FORMAT} from 'Utils/consts';
import {MAX_BOOKER_TEXT_INPUT} from 'Utils/customValueUtils';
import zoomPalette from 'zoomPalette';


/**
 * Custom Value Item Component
 * @param {Object} props
 * @return {ReactElement}
 */
function CustomValueItem(props) {
  const {t} = useTranslation();

  const radioRef = useRef();

  const {field, idx} = props;
  const {control, formState, getValues, register, setValue, trigger} = useFormContext();
  const {answerChoices, enabled, format, includeOther, name, required} = field;
  // replace all defaultVal with getValues if possible

  const fieldName = `field${idx}`;
  const formDefault = formState.defaultValues.customFields;

  // keeping track of 'other' variable in case the default value is given
  let otherVal = undefined;
  if (format === FORMAT.CHOICES_ONE) {
    if (!answerChoices.includes(formDefault[fieldName])) {
      otherVal = formDefault[fieldName];
    }
  }

  const label = capitalizeStr(name);

  // need a boolean case on whether to diplay the other
  const [checkOther, setCheckOther] = useState(!!otherVal);
  const [hasFocus, setHasFocus] = useState(false);
  const [count, setCount] = useState(0);

  // return null if question is not enabled
  if (!enabled) {
    return null;
  }

  const labelStyle = {
    '& .MuiFormLabel-asterisk': {
      color: zoomPalette.red['60'],
    },
    '& .MuiInputLabel-root': {
      'whiteSpace': 'normal',
      'wordWrap': 'break-word',
      'width': '100%',
    },
    '& .MuiFormLabel-root.MuiInputLabel-root': {
      'maxWidth': '100%',
    },
  };

  const handleLabelFocus = () => {
    // Simulate clicking the radio button
    radioRef.current.click();
  };

  return (
    <>
      {(format === FORMAT.STRING) &&
      <ZmTextField
        placeholder={t('booking.placeHolder')}
        label={label}
        sx={labelStyle}
        margin="dense"
        required={required}
        id={fieldName + '.bookerStringField'}
        type="text"
        rows={1}
        overflow="scroll"
        fullWidth
        inputProps={{
          ...register(`customFields.field${idx}`),
          maxLength: MAX_BOOKER_TEXT_INPUT,
          [`aria-label`]: label,
        }}
        aria-live="polite"
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        onChange={(e) => setCount(e.target.value?.length || 0)}
        error={!!formState.errors?.customFields?.[fieldName]?.message}
        helperText={
          formState.errors?.customFields?.[fieldName]?.message ||
          (
            // fix ZOOM-589705: fixed the bug, shouldn't need click button twice to commit
            hasFocus ?
            t('booking.remainingChar', {count: MAX_BOOKER_TEXT_INPUT - count}) : ' '
          )
        }
      />
      }
      {(format === FORMAT.TEXT) &&
      <ZmTextField
        placeholder={t('booking.placeHolder')}
        sx={labelStyle}
        label={label}
        margin="dense"
        required={required}
        id={fieldName + '.bookerTextField'}
        type="text"
        rows={2}
        multiline
        maxRows={10}
        overflow="scroll"
        fullWidth
        inputProps={{
          ...register(`customFields.field${idx}`),
          maxLength: MAX_BOOKER_TEXT_INPUT,
          [`aria-label`]: label,
          style: {
            resize: 'vertical',
          },
        }}
        aria-live="polite"
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        onChange={(e) => setCount(e.target.value?.length || 0)}
        error={!!formState.errors?.customFields?.[fieldName]?.message}
        helperText={
          formState.errors?.customFields?.[fieldName]?.message ||
          (
            // fix ZOOM-589705: fixed the bug, shouldn't need click button twice to commit
            hasFocus ?
            t('booking.remainingChar', {count: MAX_BOOKER_TEXT_INPUT - count}) : ' '
          )
        }
      />
      }
      {(format === FORMAT.SELECT) &&
      <>
        <ZmTextField
          label={label}
          margin="dense"
          fullWidth
          select
          required={required}
          id={fieldName + '.bookerSelect'}
          defaultValue={formDefault[fieldName]}
          inputProps={{
            ...register(`customFields.field${idx}`),
            [`aria-label`]: label,
          }}
          aria-live="polite"
          error={!!formState.errors?.customFields?.[fieldName]?.message}
          helperText={formState.errors?.customFields?.[fieldName]?.message}
        >
          {answerChoices.map((answerChoice) => (
            <MenuItem key={answerChoice}
              value={answerChoice}>
              {answerChoice}
            </MenuItem>
          ))}
        </ZmTextField>
      </>

      }
      {(format === FORMAT.CHOICES_ONE) &&
      <Controller
        control={control}
        name={`customFields.field${idx}`}
        defaultValue={formDefault[fieldName] || ''}
        render={({field}) => {
          return (
            <>
              <div style={{display: 'flex', marginTop: '16px', alignItems: 'center'}}>
                <FormLabel
                  required={required}
                  id={`customFields.field${idx}-rg-label`}
                  htmlFor={`customFields.field${idx}-rg`}
                  sx={{
                    '& .MuiFormLabel-asterisk': {color: zoomPalette.red['60']},
                    'color': zoomPalette.gray[90],
                    'marginRight': '10px',
                    'fontWeight': 500,
                    'fontSize': '.875rem',
                  }}>
                  <Typography variant="inherit" display="inline">
                    {label}
                  </Typography>
                </FormLabel>
                <FormHelperText error>{formState.errors?.customFields?.[fieldName]?.message}</FormHelperText>
              </div>
              <RadioGroup
                id={`customFields.field${idx}-rg`}
                required={required}
                defaultValue={formDefault[fieldName]}
                aria-labelledby={`customFields.field${idx}-rg-label`}
                aria-label={label}
                sx={{
                  '& .MuiFormLabel-root': {
                    color: 'black',
                  },
                }}
              >
                {
                  answerChoices.map((answerChoice, i) => (
                    <FormControlLabel
                      key={i}
                      value={answerChoice}
                      onClick={() => setCheckOther(false)}
                      onChange={field.onChange}
                      sx={{width: 'fit-content'}}
                      control={<ZmRadio
                        size="small"
                        sx={{padding: '8px'}}
                        id={`radioGroup${i}`}
                        checked={field.value === (answerChoice)}
                      />}
                      label={
                        <FormLabel
                          htmlFor="radioGroupLabel"
                        >
                          <Typography
                            sx={{fontSize: '.875rem',
                              fontWeight: 400,
                              lineHeight: '20px',
                            }}
                          >
                            {answerChoice}
                          </Typography>
                        </FormLabel>
                      }
                    />
                  ))
                }
                {
                  (includeOther) &&
                  <FormControlLabel
                    key={answerChoices.length}
                    checked={!!checkOther}
                    value={otherVal}
                    onClick={() => {
                      setCheckOther(true);
                      setValue(`customFields.field${idx}`, getValues(`field${idx}-otherTextfield`));
                    }}
                    sx={{width: 'fit-content'}}
                    control={
                      <ZmRadio
                        size="small"
                        sx={{padding: '8px'}}
                        id={`radioGroup${answerChoices.length}`}
                      />
                    }
                    label={
                      <Controller
                        name={`field${idx}-otherTextfield`}
                        control={control}
                        defaultValue={otherVal}
                        render={({field}) => (
                          <ZmTextField
                            {...field}
                            size="small"
                            autoFocus={checkOther}
                            margin="dense"
                            type="text"
                            required={required}
                            rows={1}
                            overflow="scroll"
                            fullWidth
                            sx={{
                              'width': '100%',
                              '.MuiFormHelperText-root.Mui-error': {
                                position: 'absolute',
                                top: '100%',
                                left: '4px',
                                margin: 0,
                                width: '500px',
                              },
                            }}
                            onFocus={handleLabelFocus}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setValue(`customFields.field${idx}`, e.target.value);
                            }}
                            aria-live="polite"
                            error={!!formState.errors?.customFields?.[fieldName]?.message}
                            helperText={formState.errors?.customFields?.[fieldName]?.message}
                          />
                        )}
                      />
                    }
                    inputRef={radioRef}
                  />
                }
              </RadioGroup>
            </>
          );
        }}/>
      }
      {(format === FORMAT.CHOICES_MANY) &&
      <Stack>
        <div style={{display: 'flex', marginTop: '16px', alignItems: 'center'}}>
          <FormLabel
            required={required}
            sx={{
              '& .MuiFormLabel-asterisk': {color: zoomPalette.red['60']},
              'color': zoomPalette.gray[90],
              'marginRight': '10px',
              'fontWeight': 500,
              'fontSize': '.875rem',
            }}>
            <Typography variant="inherit" display="inline">
              {label}
            </Typography>
          </FormLabel>
          <FormHelperText error sx={{
            color: zoomPalette.red['70'],
          }}
          >
            {formState.errors?.customFields?.[fieldName]?.message}</FormHelperText>
        </div>
        {answerChoices.map((answerChoice, i) => {
          return (
            <FormControlLabel
              sx={{
                '& .MuiFormLabel-root': {
                  color: 'black',
                },
                'width': 'fit-content',
              }}
              key={i}
              control={
                <Controller
                  name={`customFields.field${idx}.${i}.checked`}
                  control={control}
                  // defaultValue={defaultval?.indexOf(answerChoice) > -1 ? answerChoice : undefined}
                  // eslint-disable-next-line max-len
                  defaultValue={formState.defaultValues.customFields?.[`field${idx}`]?.[i]?.checked}
                  render={(renderProps) => {
                    return (
                      <ZmCheckbox
                        {...renderProps.field}
                        checked={renderProps.field.value}
                        // defaultValue={formState.defaultValues.customFields[`field${idx}`][i].checked}
                        onClick={() => {
                          trigger(`customFields.field${idx}`);
                        }}
                        sx={{
                          padding: 0,
                        }}
                      />
                    );
                  }}
                />
              }
              label={
                <FormLabel
                  htmlFor="checkboxLabel"
                >
                  <Typography
                    sx={{fontSize: '.875rem',
                      fontWeight: 400,
                      lineHeight: '20px',
                    }}
                  >
                    {answerChoice}
                  </Typography>
                </FormLabel>
              }
            />
          );
        })}
        {
          (includeOther) &&
          <FormControlLabel
            sx={{
              '& .MuiFormLabel-root': {
                color: 'black',
              },
              'width': 'fit-content',
            }}
            key={answerChoices.length}
            control={
              <Controller
                name={`customFields.field${idx}.[${answerChoices.length}].checked`}
                control={control}
                defaultValue={formDefault[fieldName][answerChoices.length]?.checked}
                render={(renderProps) => {
                  return (
                    <ZmCheckbox
                      {...renderProps.field}
                      checked={renderProps.field.value}
                      sx={{
                        padding: 0,
                      }}
                    />
                  );
                }}
              />
            }
            label={
              <RhfWatcher
                watchVals={[`customFields.field${idx}.[${answerChoices.length}].checked`]}
              >
                {
                  (isOtherChecked) => {
                    return <ZmTextField
                      size="small"
                      sx={{
                        'width': '100%',
                        '.MuiFormHelperText-root.Mui-error': {
                          position: 'absolute',
                          top: '100%',
                          left: '4px',
                          margin: 0,
                          width: '500px',
                        },
                      }}
                      autoFocus={checkOther}
                      margin="dense"
                      type="text"
                      rows={1}
                      required={required}
                      aria-live="polite"
                      overflow="scroll"
                      fullWidth
                      onChange={(e) => {
                        trigger(`customFields.field${idx}`);
                        setValue(`customFields.field${idx}.[${answerChoices.length}].value`, e.target.value);
                        setValue(`customFields.field${idx}.[${answerChoices.length}].checked`, true);
                      }}
                      defaultValue={formDefault[fieldName][answerChoices.length]?.value}
                      error={!!formState.errors?.customFields?.[fieldName]?.message}
                      helperText={formState.errors?.customFields?.[fieldName]?.message}
                    />;
                  }
                }
              </RhfWatcher>
            }
          />
        }
      </Stack>
      }
      {format === FORMAT.PHONE_NUMBER &&
      <>
        <div style={{display: 'flex', marginTop: '16px', alignItems: 'center'}}>
          <FormLabel
            sx={{
              '& .MuiFormLabel-asterisk': {color: zoomPalette.red['60']},
              'color': zoomPalette.gray[90],
              'marginRight': '10px',
              'fontWeight': 500,
              'fontSize': '.875rem',
            }}>
            {label}
          </FormLabel>
        </div>
        <Grid container spacing={2}>
          <Grid item style={{width: '140px'}}>
            <Controller
              name={`customFields.field${idx}.countryCode`}
              control={control}
              render={({field, fieldState}) => (
                <CountryInput
                  id={fieldName + 'bookerPhoneCountryCode'}
                  value={field.value}
                  onChange={field.onChange}
                  error={fieldState.error}
                />
              )}
            />
          </Grid>
          <Grid item xs>
            <Controller
              name={`customFields.field${idx}.phoneNumber`}
              control={control}
              render={({field, fieldState}) => (
                <ZmTextField
                  sx={merge(labelStyle, {
                    '& .MuiFormLabel-root.MuiInputLabel-root': {
                      textWrap: 'nowrap',
                    },
                  })}
                  label={t('booking.phoneNumber')}
                  margin="dense"
                  required={required}
                  id={fieldName + 'bookerPhoneNumber'}
                  type="text"
                  fullWidth
                  variant="outlined"
                  aria-required
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value.replace(/[^\d]/g, ''));
                    trigger(`customFields.field${idx}.countryCode`);
                  }}
                  inputProps={{
                    autoComplete: 'tel-national',
                    minLength: 5,
                    maxLength: 20,
                  }}
                  aria-live="polite"
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </>
      }
    </>
  );
}


/**
 * Custom Fields Component
 * @param {Object} props
 * @return {ReactElement}
 */
export default function CustomValuesWidget(props) {
  const {customFields, isRouting} = props;

  // pass in custom value with the matching index, set to the default value
  return (
    <Stack>
      {customFields.map((field, idx) => {
        return <CustomValueItem
          field={field}
          idx={idx}
          key={idx}
          isRouting={isRouting}
        />;
      })}
    </Stack>
  );
}
